import { Link, useLocation, useNavigate, useParams } from 'react-router-dom'
import projectsData from '@/content/projects.json'
import experiencesData from '@/content/experiences.json'
import educationData from '@/content/education.json'
import { Bar } from 'react-chartjs-2'
import {
  BarElement,
  CategoryScale,
  Chart as ChartJS,
  Legend,
  LinearScale,
  LogarithmicScale,
  Title,
  Tooltip,
  type ChartOptions
} from 'chart.js'
import StatusPill, { type ItemStatus } from '@/components/ui/StatusPill'

ChartJS.register(CategoryScale, LinearScale, LogarithmicScale, BarElement, Title, Tooltip, Legend)

type Variant = 'default' | 'lite'
type Kind = 'project' | 'experience' | 'education'

type ArticleListItem = string | { text: string; children?: ArticleListItem[] }

type ArticleBlock =
  | { type: 'p'; text: string }
  | { type: 'subtitle'; text: string }
  | { type: 'ul'; items: ArticleListItem[] }
  | { type: 'ol'; items: ArticleListItem[] }

type ArticleSection =
  | { title: string; text: string; bullets?: never; blocks?: never; subsections?: never }
  | { title: string; bullets: ArticleListItem[]; text?: never; blocks?: never; subsections?: never }
  | { title: string; blocks: ArticleBlock[]; text?: never; bullets?: never; subsections?: ArticleSection[] }
  | { title: string; subsections: ArticleSection[]; text?: never; bullets?: never; blocks?: never }

type ArticleMedia = { src: string; alt: string; caption?: string }
type ArticleLink = { label: string; url: string; kind?: 'repo' | 'demo' | 'doc' | 'external' }

type ArticleChartDataset = {
  label: string
  data: number[]
  backgroundColor?: string | string[]
  borderColor?: string | string[]
}

type ArticleChart = {
  title: string
  kind: 'bar'
  labels: string[]
  datasets: ArticleChartDataset[]
  note?: string
  scale?: 'linear' | 'log'
}

type Article = {
  intro?: string
  sections?: ArticleSection[]
  media?: ArticleMedia[]
  charts?: ArticleChart[]
  links?: ArticleLink[]
}

type Project = {
  slug: string
  title: string
  desc: string
  tech?: string[]
  repo?: string
  demo?: string
  cover?: string
  cover3d?: string
  tags?: string[]
  preview?: boolean
  status?: ItemStatus
  article?: Article
}

type Experience = {
  slug: string
  role: string
  company: string
  start: string
  end: string
  status?: ItemStatus
  bullets?: string[]
  logo?: string
  article?: Article
}

type Education = {
  slug: string
  school: string
  degree: string
  start: string
  end: string
  status?: ItemStatus
  logo?: string
  specialization?: string
  content?: string[]
  majorProjects?: { title: string; slug: string }[]
  article?: Article
}

function toPublicUrl(path?: string) {
  if (!path) return ''
  const p = path.replace(/^\/?public\//, '/')
  return p.startsWith('/') ? p : `/${p}`
}

function isVideoSrc(src: string) {
  return /\.(mp4|webm|ogg)$/i.test(src)
}

function pickVariantFromLocationState(state: unknown): Variant {
  if (!state || typeof state !== 'object') return 'default'
  const maybe = (state as { variant?: Variant }).variant
  return maybe === 'lite' ? 'lite' : 'default'
}

function SectionIcon({ title, variant }: { title: string; variant: Variant }) {
  const isLite = variant === 'lite'
  const stroke = isLite ? 'rgba(226,232,240,0.7)' : 'rgba(226,232,240,0.6)'
  const fill = isLite ? 'rgba(148,163,184,0.12)' : 'rgba(226,232,240,0.08)'
  const t = title.toLowerCase()
  const kind =
    t.includes('stack') || t.includes('tech')
      ? 'stack'
      : t.includes('mission')
        ? 'missions'
        : t.includes('réalis') || t.includes('realis') || t.includes('résultat') || t.includes('resultat')
          ? 'achievements'
          : t.includes('méthod') || t.includes('method')
            ? 'method'
            : t.includes('contexte') || t.includes('organisme') || t.includes('entreprise')
              ? 'context'
              : 'section'

  const common = {
    width: 18,
    height: 18,
    viewBox: '0 0 24 24',
    fill: 'none' as const,
    xmlns: 'http://www.w3.org/2000/svg'
  }

  if (kind === 'stack') {
    return (
      <svg {...common} aria-hidden="true">
        <path d="M12 2 3 7l9 5 9-5-9-5Z" stroke={stroke} strokeWidth="1.6" />
        <path d="M3 12l9 5 9-5" stroke={stroke} strokeWidth="1.6" />
        <path d="M3 17l9 5 9-5" stroke={stroke} strokeWidth="1.6" />
      </svg>
    )
  }

  if (kind === 'missions') {
    return (
      <svg {...common} aria-hidden="true">
        <path d="M9 6h11" stroke={stroke} strokeWidth="1.6" strokeLinecap="round" />
        <path d="M9 12h11" stroke={stroke} strokeWidth="1.6" strokeLinecap="round" />
        <path d="M9 18h11" stroke={stroke} strokeWidth="1.6" strokeLinecap="round" />
        <path
          d="M4.5 6.5l1 1 2-2"
          stroke={stroke}
          strokeWidth="1.6"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
        <path
          d="M4.5 12.5l1 1 2-2"
          stroke={stroke}
          strokeWidth="1.6"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
        <path
          d="M4.5 18.5l1 1 2-2"
          stroke={stroke}
          strokeWidth="1.6"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
      </svg>
    )
  }

  if (kind === 'achievements') {
    return (
      <svg {...common} aria-hidden="true">
        <path
          d="M7 4h10v3a5 5 0 0 1-10 0V4Z"
          stroke={stroke}
          strokeWidth="1.6"
          strokeLinejoin="round"
        />
        <path d="M12 12v3" stroke={stroke} strokeWidth="1.6" strokeLinecap="round" />
        <path d="M9 20h6" stroke={stroke} strokeWidth="1.6" strokeLinecap="round" />
        <path d="M17 5h2a2 2 0 0 1-2 2" stroke={stroke} strokeWidth="1.6" strokeLinecap="round" />
        <path d="M7 5H5a2 2 0 0 0 2 2" stroke={stroke} strokeWidth="1.6" strokeLinecap="round" />
      </svg>
    )
  }

  if (kind === 'method') {
    return (
      <svg {...common} aria-hidden="true">
        <path d="M4 19V5a2 2 0 0 1 2-2h10a2 2 0 0 1 2 2v14" stroke={stroke} strokeWidth="1.6" />
        <path d="M8 7h8" stroke={stroke} strokeWidth="1.6" strokeLinecap="round" />
        <path d="M8 11h8" stroke={stroke} strokeWidth="1.6" strokeLinecap="round" />
        <path d="M8 15h5" stroke={stroke} strokeWidth="1.6" strokeLinecap="round" />
      </svg>
    )
  }

  if (kind === 'context') {
    return (
      <svg {...common} aria-hidden="true">
        <path d="M12 21s7-4.5 7-11a7 7 0 1 0-14 0c0 6.5 7 11 7 11Z" stroke={stroke} strokeWidth="1.6" />
        <path
          d="M12 10.5a2 2 0 1 0 0-4 2 2 0 0 0 0 4Z"
          fill={fill}
          stroke={stroke}
          strokeWidth="1.6"
        />
      </svg>
    )
  }

  return (
    <svg {...common} aria-hidden="true">
      <path d="M12 2v20" stroke={stroke} strokeWidth="1.6" strokeLinecap="round" />
      <path d="M2 12h20" stroke={stroke} strokeWidth="1.6" strokeLinecap="round" />
    </svg>
  )
}

function ChartsView({ charts, variant }: { charts?: ArticleChart[]; variant: Variant }) {
  if (!charts || charts.length === 0) return null
  const isLite = variant === 'lite'

  const tickColor = isLite ? 'rgba(226,232,240,0.8)' : 'rgba(226,232,240,0.75)'
  const gridColor = isLite ? 'rgba(148,163,184,0.18)' : 'rgba(148,163,184,0.12)'

  const palette = [
    'rgba(59, 130, 246, 0.75)', // blue-500
    'rgba(16, 185, 129, 0.70)', // emerald-500
    'rgba(168, 85, 247, 0.70)' // purple-500
  ]

  return (
    <div className="space-y-3">
      <div className={isLite ? 'text-xs tracking-widest uppercase text-slate-400' : 'text-xs tracking-widest uppercase text-slate-400'}>
        En chiffres
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {charts.map((c, idx) => {
          const options: ChartOptions<'bar'> = {
            responsive: true,
            maintainAspectRatio: false,
            indexAxis: 'y',
            plugins: {
              legend: { display: c.datasets.length > 1 },
              title: { display: false },
              tooltip: { enabled: true }
            },
            scales: {
              x:
                c.scale === 'log'
                  ? {
                      type: 'logarithmic',
                      ticks: { color: tickColor },
                      grid: { color: gridColor }
                    }
                  : {
                      type: 'linear',
                      ticks: { color: tickColor },
                      grid: { color: gridColor }
                    },
              y: {
                ticks: { color: tickColor },
                grid: { display: false }
              }
            }
          }

          const data = {
            labels: c.labels,
            datasets: c.datasets.map((d, i) => ({
              ...d,
              backgroundColor: d.backgroundColor ?? palette[i % palette.length],
              borderRadius: 10,
              borderSkipped: false as const
            }))
          }

          return (
            <div
              key={c.title + idx}
              className={
                isLite
                  ? 'lite-card rounded-2xl overflow-hidden'
                  : 'rounded-xl overflow-hidden border border-slate-800 bg-slate-900/40'
              }
            >
              <div className="px-4 pt-4">
                <div className={isLite ? 'text-sm font-bold text-slate-100' : 'text-sm font-semibold text-slate-100'}>
                  {c.title}
                </div>
                {c.note && <div className="mt-1 text-xs text-slate-400">{c.note}</div>}
              </div>
              <div className="p-4 pt-3">
                <div className={isLite ? 'h-56' : 'h-56'}>
                  {c.kind === 'bar' && <Bar options={options} data={data} />}
                </div>
              </div>
            </div>
          )
        })}
      </div>
    </div>
  )
}

function ArticleView({ article, variant }: { article?: Article; variant: Variant }) {
  if (!article) return null
  const isLite = variant === 'lite'

  return (
    <div className="space-y-10">
      {article.intro && (
        <p className={isLite ? 'text-slate-300 leading-relaxed' : 'text-slate-300 leading-relaxed'}>
          {article.intro}
        </p>
      )}

      <ChartsView charts={article.charts} variant={variant} />

      {article.media && article.media.length > 0 && (
        <div className="space-y-3">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {article.media.map((m) => (
              <figure
                key={m.src}
                className={
                  isLite
                    ? 'lite-card rounded-2xl overflow-hidden'
                    : 'rounded-xl overflow-hidden border border-slate-800 bg-slate-900/40'
                }
              >
                {isVideoSrc(m.src) ? (
                  <video
                    src={toPublicUrl(m.src)}
                    controls
                    preload="metadata"
                    playsInline
                    aria-label={m.alt}
                    className={isLite ? 'h-56 w-full object-cover lite-media' : 'h-56 w-full object-cover'}
                  >
                    Your browser does not support the video tag.
                  </video>
                ) : (
                  <img
                    src={toPublicUrl(m.src)}
                    alt={m.alt}
                    loading="lazy"
                    decoding="async"
                    className={isLite ? 'h-56 w-full object-cover lite-media' : 'h-56 w-full object-cover'} // replace object-cover by object-contain
                  />
                )}
                {m.caption && (
                  <figcaption className={isLite ? 'px-4 py-3 text-xs text-slate-400' : 'px-4 py-3 text-xs text-slate-400'}>
                    {m.caption}
                  </figcaption>
                )}
              </figure>
            ))}
          </div>
        </div>
      )}

      {article.sections && article.sections.length > 0 && (
        <div className="space-y-8">
          {article.sections.map((s, idx) => (
            <section key={s.title + idx} className="space-y-3">
              <div className="flex items-center gap-2">
                <SectionIcon title={s.title} variant={variant} />
                <h2 className={isLite ? 'text-lg font-bold text-slate-100' : 'text-lg font-semibold text-slate-100'}>
                  {s.title}
                </h2>
              </div>

              {'text' in s && (
                <p className={isLite ? 'text-slate-300 leading-relaxed' : 'text-slate-300 leading-relaxed'}>
                  {s.text}
                </p>
              )}

              {'bullets' in s && (
                <UnorderedListView items={s.bullets ?? []} variant={variant} />
              )}

              {'blocks' in s && <BlocksView blocks={s.blocks ?? []} variant={variant} />}

              {'subsections' in s && <SubsectionsView subsections={s.subsections ?? []} variant={variant} />}
            </section>
          ))}
        </div>
      )}

      {article.links && article.links.length > 0 && (
        <div className="space-y-3">
          <div className={isLite ? 'text-xs tracking-widest uppercase text-slate-400' : 'text-xs tracking-widest uppercase text-slate-400'}>
            Liens
          </div>
          <div className="flex flex-wrap gap-2">
            {article.links.map((l) => (
              <a
                key={l.url}
                href={l.url}
                target="_blank"
                rel="noreferrer"
                className={
                  isLite
                    ? 'rounded-xl border border-slate-700 bg-slate-950/30 px-3 py-2 text-sm font-semibold text-slate-100 hover:bg-slate-950/55 transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-500/40'
                    : 'rounded-lg border border-slate-700 bg-slate-900/40 px-3 py-2 text-sm text-slate-200 hover:bg-slate-800 transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-500/50'
                }
              >
                {l.label}
              </a>
            ))}
          </div>
        </div>
      )}
    </div>
  )
}

function normalizeListItem(item: ArticleListItem): { text: string; children?: ArticleListItem[] } {
  return typeof item === 'string' ? { text: item } : item
}

function UnorderedListView({
  items,
  variant,
  depth = 0
}: {
  items: ArticleListItem[]
  variant: Variant
  depth?: number
}) {
  const isLite = variant === 'lite'
  const spacing = depth === 0 ? 'space-y-2' : 'mt-2 space-y-1'
  const indent = depth === 0 ? '' : 'pl-5'
  const dotSize = depth === 0 ? 'h-1.5 w-1.5' : 'h-1 w-1'

  return (
    <ul className={`${spacing} ${indent} ${isLite ? 'text-slate-300' : 'text-slate-300'}`}>
      {items.map((raw, i) => {
        const it = normalizeListItem(raw)
        return (
          <li key={i} className="flex gap-3">
            <span className={`mt-2 ${dotSize} rounded-full bg-slate-500 flex-none`} />
            <div className="min-w-0">
              <div className="leading-relaxed">{it.text}</div>
              {it.children && it.children.length > 0 && (
                <div className="min-w-0">
                  <UnorderedListView items={it.children} variant={variant} depth={depth + 1} />
                </div>
              )}
            </div>
          </li>
        )
      })}
    </ul>
  )
}

function OrderedListView({
  items,
  variant,
  depth = 0
}: {
  items: ArticleListItem[]
  variant: Variant
  depth?: number
}) {
  const isLite = variant === 'lite'
  const spacing = depth === 0 ? 'space-y-2' : 'mt-2 space-y-1'
  const indent = depth === 0 ? '' : 'pl-6'

  return (
    <ol className={`${spacing} ${indent} list-decimal ${isLite ? 'text-slate-300' : 'text-slate-300'}`}>
      {items.map((raw, i) => {
        const it = normalizeListItem(raw)
        return (
          <li key={i} className="ml-4">
            <div className="leading-relaxed">{it.text}</div>
            {it.children && it.children.length > 0 && (
              <div className="min-w-0">
                <OrderedListView items={it.children} variant={variant} depth={depth + 1} />
              </div>
            )}
          </li>
        )
      })}
    </ol>
  )
}

function BlocksView({ blocks, variant }: { blocks: ArticleBlock[]; variant: Variant }) {
  const isLite = variant === 'lite'
  return (
    <div className="space-y-3">
      {blocks.map((b, i) => {
        if (b.type === 'p') {
          return (
            <p key={i} className={isLite ? 'text-slate-300 leading-relaxed' : 'text-slate-300 leading-relaxed'}>
              {b.text}
            </p>
          )
        }
        if (b.type === 'subtitle') {
          return (
            <h3 key={i} className={isLite ? 'text-sm font-bold text-slate-100' : 'text-sm font-semibold text-slate-100'}>
              {b.text}
            </h3>
          )
        }
        if (b.type === 'ul') return <UnorderedListView key={i} items={b.items} variant={variant} />
        if (b.type === 'ol') return <OrderedListView key={i} items={b.items} variant={variant} />
        return null
      })}
    </div>
  )
}

function SubsectionsView({ subsections, variant }: { subsections: ArticleSection[]; variant: Variant }) {
  const isLite = variant === 'lite'
  return (
    <div className="space-y-6">
      {subsections.map((sub, idx) => (
        <div key={sub.title + idx} className="space-y-2">
          <h3 className={isLite ? 'text-base font-bold text-slate-100' : 'text-base font-semibold text-slate-100'}>
            {sub.title}
          </h3>
          {'text' in sub && (
            <p className={isLite ? 'text-slate-300 leading-relaxed' : 'text-slate-300 leading-relaxed'}>{sub.text}</p>
          )}
          {'bullets' in sub && <UnorderedListView items={sub.bullets ?? []} variant={variant} />}
          {'blocks' in sub && <BlocksView blocks={sub.blocks ?? []} variant={variant} />}
          {'subsections' in sub && <SubsectionsView subsections={sub.subsections ?? []} variant={variant} />}
        </div>
      ))}
    </div>
  )
}

function NotFound({ variant }: { variant: Variant }) {
  const isLite = variant === 'lite'
  return (
    <div className={isLite ? 'lite-card rounded-2xl p-6' : 'rounded-xl border border-slate-800 bg-slate-900/40 p-6'}>
      <div className={isLite ? 'text-slate-100 font-bold' : 'text-slate-100 font-semibold'}>Élément introuvable</div>
      <div className={isLite ? 'mt-2 text-slate-300' : 'mt-2 text-slate-300'}>
        Le lien semble invalide ou l’élément n’existe plus.
      </div>
      <div className="mt-4">
        <Link
          to={isLite ? '/lite' : '/'}
          className={
            isLite
              ? 'text-sm font-semibold text-brand-300 hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-500/40 rounded'
              : 'text-sm text-brand-400 hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-500/50 rounded'
          }
        >
          Retour à l’accueil
        </Link>
      </div>
    </div>
  )
}

export default function PortfolioDetail({ mode = 'page' }: { mode?: 'page' | 'modal' }) {
  const { kind, slug } = useParams()
  const navigate = useNavigate()
  const location = useLocation()
  const variant = pickVariantFromLocationState(location.state)
  const isLite = variant === 'lite'
  const isModal = mode === 'modal'

  const projects = projectsData as Project[]
  const experiences = experiencesData as Experience[]
  const education = educationData as Education[]

  const k = (kind ?? '') as Kind
  const s = slug ?? ''

  const found =
    k === 'project'
      ? projects.find((p) => p.slug === s)
      : k === 'experience'
        ? experiences.find((e) => e.slug === s)
        : k === 'education'
          ? education.find((e) => e.slug === s)
          : null

  const title =
    k === 'project'
      ? (found as Project | null)?.title
      : k === 'experience'
        ? `${(found as Experience | null)?.role ?? ''} — ${(found as Experience | null)?.company ?? ''}`.trim()
        : k === 'education'
          ? `${(found as Education | null)?.degree ?? ''} — ${(found as Education | null)?.school ?? ''}`.trim()
          : ''

  const period = found && 'start' in found && 'end' in found ? `${found.start} — ${found.end}` : undefined
  const cover = k === 'project' ? toPublicUrl((found as Project | null)?.cover) : ''
  const status =
    k === 'project'
      ? (found as Project | null)?.status
      : k === 'experience'
        ? (found as Experience | null)?.status
        : k === 'education'
          ? (found as Education | null)?.status
          : undefined

  const topLinks: ArticleLink[] = []
  if (k === 'project') {
    const p = found as Project | null
    if (p?.repo) topLinks.push({ label: 'Code', url: p.repo, kind: 'repo' })
    if (p?.demo) topLinks.push({ label: 'Demo', url: p.demo, kind: 'demo' })
  }

  const content = (
    <div className="mx-auto max-w-4xl px-4 py-10">
      <div className="mb-6 flex items-center gap-3">
        <button
          type="button"
          onClick={() => navigate(-1)}
          className={
            isLite
              ? 'rounded-xl border border-slate-700 bg-slate-950/30 px-3 py-2 text-sm font-semibold text-slate-100 hover:bg-slate-950/55 transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-500/40'
              : 'rounded-lg border border-slate-700 bg-slate-900/40 px-3 py-2 text-sm text-slate-200 hover:bg-slate-800 transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-500/50'
          }
        >
          Retour
        </button>
        {!isModal && (
          <Link
            to={isLite ? '/lite' : '/'}
            className={
              isLite
                ? 'text-sm font-semibold text-brand-300 hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-500/40 rounded'
                : 'text-sm text-brand-400 hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-500/50 rounded'
            }
          >
            Accueil
          </Link>
        )}
      </div>

      <article
        className={
          isLite
            ? 'lite-card rounded-3xl overflow-hidden'
            : 'rounded-2xl overflow-hidden border border-slate-800 bg-slate-900/40'
        }
      >
        {cover ? (
          <div className={isLite ? 'bg-white/40' : 'bg-slate-800/40'}>
            <img
              src={cover}
              alt={title || 'Projet'}
              loading="lazy"
              decoding="async"
              className={isLite ? 'h-64 w-full object-contain lite-media' : 'h-64 w-full object-contain'}
            />
          </div>
        ) : (
          <div className={isLite ? 'h-10 bg-white/40' : 'h-10 bg-slate-800/40'} />
        )}

        <div className="p-6 md:p-8 space-y-6">
          <header className="space-y-2">
            <div className={isLite ? 'text-xs tracking-widest uppercase text-slate-400' : 'text-xs tracking-widest uppercase text-slate-400'}>
              {k === 'project' ? 'Projet' : k === 'experience' ? 'Expérience' : k === 'education' ? 'Formation' : 'Portfolio'}
            </div>
            <h1 className={isLite ? 'text-2xl md:text-3xl font-extrabold italic tracking-tight text-slate-100' : 'text-2xl md:text-3xl font-semibold text-slate-100'}>
              {title || 'Détail'}
            </h1>
            <div className="flex flex-wrap items-center gap-x-3 gap-y-2">
              {period && <div className={isLite ? 'text-sm text-slate-400' : 'text-sm text-slate-400'}>{period}</div>}
              <StatusPill status={status} variant={variant} />
            </div>
          </header>

          {k === 'project' && (found as Project | null)?.desc && (
            <p className={isLite ? 'text-slate-300 leading-relaxed' : 'text-slate-300 leading-relaxed'}>
              {(found as Project).desc}
            </p>
          )}

          {k === 'project' && (found as Project | null)?.tech && (found as Project).tech!.length > 0 && (
            <div className="flex flex-wrap gap-2">
              {(found as Project).tech!.map((t) => (
                <span
                  key={t}
                  className={
                    isLite
                      ? 'rounded-xl border border-slate-700 bg-slate-950/25 px-2.5 py-1 text-xs text-slate-300'
                      : 'rounded bg-slate-800 px-2.5 py-1 text-xs text-slate-200'
                  }
                >
                  {t}
                </span>
              ))}
            </div>
          )}

          {topLinks.length > 0 && (
            <div className="flex flex-wrap gap-2">
              {topLinks.map((l) => (
                <a
                  key={l.url}
                  href={l.url}
                  target="_blank"
                  rel="noreferrer"
                  className={
                    isLite
                      ? 'rounded-xl border border-black/10 bg-white/60 px-3 py-2 text-sm font-semibold text-slate-800 hover:bg-white/80 transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#2b6cb0]/50'
                      : 'rounded-lg border border-slate-700 bg-slate-900/40 px-3 py-2 text-sm text-slate-200 hover:bg-slate-800 transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-500/50'
                  }
                >
                  {l.label}
                </a>
              ))}
            </div>
          )}

          {!found ? (
            <NotFound variant={variant} />
          ) : (
            <ArticleView
              variant={variant}
              article={
                (k === 'project'
                  ? (found as Project).article
                  : k === 'experience'
                    ? (found as Experience).article
                    : (found as Education).article) ?? undefined
              }
            />
          )}
        </div>
      </article>
    </div>
  )

  if (!isModal) {
    return <div className={isLite ? 'lite lite-paper min-h-full text-slate-100' : 'min-h-full'}>{content}</div>
  }

  return (
    <div className="fixed inset-0 z-50">
      <button
        type="button"
        aria-label="Fermer"
        className={isLite ? 'absolute inset-0 bg-black/30' : 'absolute inset-0 bg-black/60'}
        onClick={() => navigate(-1)}
      />
      <div className="absolute inset-0 overflow-y-auto" onClick={() => navigate(-1)}>
        <div className="min-h-full flex items-start justify-center p-4 md:p-8">
          <div className="w-full max-w-5xl" onClick={(e) => e.stopPropagation()}>
            {content}
          </div>
        </div>
      </div>
    </div>
  )
}

